import React, { createContext, useContext } from "react";
import { makeApiCall, fetchPolling, isEmpty } from "@swiggy-private/js-utils";
import { APIEvent, ApiResponse } from "@swiggy-private/js-utils/dist/fetch/types";
import { COMMON_LOGIN_STATUS_ERROR_CODE, IApiProviderProps, loginHelper } from "./helpers";

interface IApiContext {
    makeApiCall: typeof makeApiCall | undefined;
    fetchPolling: typeof fetchPolling | undefined;
    fetchPLPolling: typeof fetchPolling | undefined;
    makePlApiCall: typeof makeApiCall | undefined;
    sendEvent?: APIEvent;
}

const DEFAULT_INITIAL_VALUE = {
    makeApiCall: undefined,
    fetchPolling: undefined,
    makePlApiCall: undefined,
    fetchPLPolling: undefined,
    sendEvent: undefined,
};

const ApiContext = createContext<IApiContext>(DEFAULT_INITIAL_VALUE);

export const ApiProvider: React.FC<React.PropsWithChildren<IApiProviderProps>> = ({
    children,
    authCallback,
    forwardPath,
    sdk,
    sendEvent,
}) => {
    const Caller: typeof makeApiCall = async (...props) => {
        try {
            const [params, swgyHeaders, event, ...rest] = props;
            const data = await makeApiCall(params, swgyHeaders, sendEvent || event, ...rest);
            if (COMMON_LOGIN_STATUS_ERROR_CODE.includes(data.statusCode)) {
                loginHelper({
                    authCallback,
                    forwardPath,
                    sdk,
                });
                return { statusCode: data.statusCode, data: {} as typeof data.data };
            } else {
                if (!isEmpty(data)) {
                    return {
                        statusCode: data?.statusCode ?? 0,
                        data: data.data as unknown as typeof data.data,
                    };
                }
                return { statusCode: 1, data: {} as typeof data.data };
            }
        } catch (err) {
            throw err;
        }
    };
    const PollingCaller: typeof fetchPolling = async (props) => {
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return await fetchPolling<any>({
                ...props,
                sendEvent: sendEvent || props.sendEvent,
                shouldContinuePolling: (data2) =>
                    COMMON_LOGIN_STATUS_ERROR_CODE.includes(data2.statusCode),
                onPollingResponse: (data) => {
                    if (COMMON_LOGIN_STATUS_ERROR_CODE.includes(data.statusCode)) {
                        loginHelper({
                            authCallback,
                            forwardPath,
                            sdk,
                        });
                        props.onPollingResponse?.({
                            statusCode: data.statusCode,
                            data: {} as typeof data.data,
                        });
                    } else {
                        props.onPollingResponse?.(data);
                    }
                },
            });
        } catch (err) {
            throw err;
        }
    };

    const PollingPLCaller: typeof fetchPolling = async (props) => {
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return await fetchPolling<any>({
                ...props,
                sendEvent: sendEvent || props.sendEvent,
                shouldContinuePolling: props.shouldContinuePolling,
                onPollingResponse: (apiResponse) => {
                    const { success } = apiResponse as unknown as ApiResponse<
                        { cards: [] },
                        unknown
                    >;
                    if (!isEmpty(success) && !isEmpty(success.cards)) {
                        props.onPollingResponse?.({
                            ...apiResponse,
                            statusCode: 0,
                        });
                    } else {
                        props.onPollingResponse?.({
                            ...apiResponse,
                            statusCode: 1,
                        });
                    }
                },
            });
        } catch (err) {
            throw err;
        }
    };

    const PlCaller: typeof makeApiCall = async (...props) => {
        try {
            const [params, swgyHeaders, event, ...rest] = props;
            const apiResponse = await makeApiCall(params, swgyHeaders, sendEvent || event, ...rest);
            const { success } = apiResponse as unknown as ApiResponse<{ cards: [] }, unknown>;
            if (!isEmpty(success) && !isEmpty(success.cards)) {
                return {
                    ...apiResponse,
                    statusCode: apiResponse?.statusCode ?? 0,
                };
            } else {
                return {
                    ...apiResponse,
                    statusCode: 1,
                };
            }
        } catch (err) {
            throw err;
        }
    };
    return (
        <ApiContext.Provider
            value={{
                makeApiCall: Caller,
                fetchPolling: PollingCaller,
                makePlApiCall: PlCaller,
                fetchPLPolling: PollingPLCaller,
                sendEvent,
            }}
        >
            {children}
        </ApiContext.Provider>
    );
};

export const useApiContextFetch = () => {
    return useContext(ApiContext).makeApiCall;
};

export const usePlApiContextFetch = () => {
    return useContext(ApiContext).makePlApiCall;
};

export const useApiContextFetchPolling = () => {
    return useContext(ApiContext).fetchPolling;
};

export const useApiContextFetchPLPolling = () => {
    return useContext(ApiContext).fetchPLPolling;
};

export const useApiContext = () => {
    return useContext(ApiContext);
};
